import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {css, Theme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {elevation} from "@pg-design/elevation";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    color,
    flex,
    flexDirection,
    h100,
    imageResponsive,
    m,
    ml,
    mr,
    p,
    pb,
    position,
    pv
} from "@pg-design/helpers-css";
import {BrandBuildingsIcon, BrandCalendarIcon, MapMarkerOutlineIcon, PriceIcon, ProgressIcon} from "@pg-design/icons";
import {Image} from "@pg-design/image";
import {Loader} from "@pg-design/loader";
import {Text} from "@pg-design/text";

import {formatDate} from "../../../../../utils/dates";
import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {truncate} from "../../css_helpers/truncate";
import {IOfferBoxOffer, OfferType} from "../mocks/singleOfferMock";
import {BoxPlotChart} from "./BoxPlotChart";
import {ConstructionProgressBar} from "./ConstructionProgressBar";
import {RoomsStructureChart} from "./RoomsStructureChart";
import {SalesProgressBar} from "./SalesProgressBar";

export interface IOfferBoxProps {
    offer: IOfferBoxOffer | null;
}

const getOfferTypeNameKey = (offerType: OfferType): string => {
    switch (offerType) {
        case OfferType.FLAT:
            return "offer_box.property_type_flat";
        case OfferType.HOUSE:
            return "offer_box.property_type_house";
        case OfferType.COMMERCIAL:
            return "offer_box.property_type_commercial";
        default:
            throw new Error("Unknown offer type");
    }
};

export const OfferBox = ({offer}: IOfferBoxProps) => {
    const {t} = useTranslation();

    const scrollToTop = () => {
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 0);
    };

    return (
        <div css={offerBoxBase}>
            {offer ? (
                <>
                    <div css={offerBoxHeader}>
                        <div css={offerBoxImageLink}>
                            <div css={offerBoxImageWrapper}>
                                <Image
                                    alt={offer.name}
                                    src={offer.main_image.m_img_375x211}
                                    css={offerImage}
                                    width={String(offer.main_image.width)}
                                    height={String(offer.main_image.height)}
                                />

                                {offer.stats.ranges_price_m2_min > 0 ? (
                                    <div css={offerBoxPriceHolder}>
                                        <span>{t("offer_box.from")}</span> {offer.stats.ranges_price_m2_min} zł/m
                                        <sup>2</sup>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div css={offerBoxHeaderInfo}>
                            <Text as="h2" variant="headline_6" strong css={truncate}>
                                {offer.name}
                            </Text>

                            <Text as="h3" variant="info_txt_1" css={[pv(0.5), truncate]}>
                                <span css={offerBoxVendor}>{offer.vendor.name}</span>
                            </Text>

                            <div css={offerBoxAddressWrapper}>
                                <MapMarkerOutlineIcon size="2.4" css={mr(2)} />
                                <Text variant="body_copy_2">
                                    {offer.street_name && (
                                        <>
                                            {offer.street_name} {offer.street_number && offer.street_number}
                                        </>
                                    )}
                                    {!offer.street_name && offer.region.short_name_reverted}
                                </Text>
                            </div>
                            <div css={flex("center", "start")}>
                                <BrandBuildingsIcon size="2.4" css={mr(1.5)} />
                                <div>
                                    <Text variant="info_txt_1">{t("offer_box.property_type")}</Text>
                                    <Text variant="info_txt_1" strong>
                                        {t(getOfferTypeNameKey(offer.type))}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div css={[p(2)]}>
                        <div css={topSectionsRow}>
                            {/* Przedział cen */}
                            <div css={offerBoxSection}>
                                <div css={offerBoxSectionHeader}>
                                    <PriceIcon size="2.4" />
                                    <Text variant="body_copy_2" strong>
                                        {t("offer_box.price_range")}
                                    </Text>
                                </div>
                                <div>
                                    <div css={offerBoxPriceRow}>
                                        <span css={offerBoxLabel}>{t("offer_box.price_per_m2")}</span>
                                        <span css={offerBoxValue}>
                                            {offer.stats.ranges_price_m2_min > 0 && offer.stats.ranges_price_m2_max ? (
                                                <>
                                                    <b>
                                                        {offer.stats.ranges_price_m2_min.toLocaleString("pl-PL")}-
                                                        {offer.stats.ranges_price_m2_max.toLocaleString("pl-PL")}
                                                    </b>{" "}
                                                    zł/m
                                                    <sup>2</sup>
                                                </>
                                            ) : (
                                                <b>{t("offer_box.no_data")}</b>
                                            )}
                                        </span>
                                    </div>
                                    <div css={offerBoxPriceRow}>
                                        <span css={offerBoxLabel}>{t("offer_box.area")}</span>
                                        <span css={offerBoxValue}>
                                            <b>
                                                {offer.stats.ranges_area_min === offer.stats.ranges_area_max
                                                    ? offer.stats.ranges_area_min
                                                    : `${offer.stats.ranges_area_min}-${offer.stats.ranges_area_max}`}
                                            </b>{" "}
                                            m<sup>2</sup>
                                        </span>
                                    </div>

                                    {offer.stats.properties_avg_price_m2_all > 0 &&
                                        offer.stats.ranges_price_m2_min > 0 &&
                                        offer.stats.ranges_price_m2_max > 0 && (
                                            <div css={priceChartWrapper}>
                                                <BoxPlotChart
                                                    min={offer.stats.ranges_price_m2_min}
                                                    max={offer.stats.ranges_price_m2_max}
                                                    average={offer.stats.properties_avg_price_m2_all}
                                                />
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* Struktura mieszkań */}
                            {offer.stats.rooms_structure && offer.stats.rooms_structure.length > 0 && (
                                <div css={offerBoxSection}>
                                    <div css={offerBoxSectionHeader}>
                                        <Text variant="body_copy_2" strong>
                                            {t("offer_box.rooms_structure_title")}
                                        </Text>
                                    </div>
                                    <RoomsStructureChart data={offer.stats.rooms_structure!} />
                                </div>
                            )}
                        </div>

                        {/* Postęp budowy */}
                        <div css={offerBoxSection}>
                            <div css={offerBoxSectionHeader}>
                                <BrandCalendarIcon size="2.4" />
                                <Text variant="body_copy_2" strong>
                                    {t("offer_box.construction_progress")}
                                </Text>
                            </div>
                            <div>
                                <Text variant="body_copy_2" css={(pb(1), mr(1))} as="span">
                                    {t("offer_box.start")}{" "}
                                    <b>{formatDate(offer.construction_date_range.lower, "Q 'kw.' yyyy")}</b>
                                </Text>
                                <Text variant="body_copy_2" as="span">
                                    {t("offer_box.handover")}{" "}
                                    <b>{formatDate(offer.construction_date_range.upper, "Q 'kw.' yyyy")}</b>
                                </Text>

                                <ConstructionProgressBar
                                    startDate={offer.construction_date_range.lower}
                                    endDate={offer.construction_date_range.upper}
                                    formatDate={formatDate}
                                />
                            </div>
                        </div>

                        {/* Postęp sprzedaży */}
                        <div css={offerBoxSection}>
                            <div css={offerBoxSectionHeader}>
                                <ProgressIcon size="2.4" />
                                <Text variant="body_copy_2" strong>
                                    {t("offer_box.sales_progress")}
                                </Text>
                            </div>
                            <div>
                                <Text variant="info_txt_1">
                                    {t("offer_box.units_count")}{" "}
                                    <b>
                                        {offer.stats.properties_count_for_sale != null &&
                                        !isNaN(offer.stats.properties_count_for_sale)
                                            ? offer.stats.properties_count_for_sale
                                            : "-"}
                                    </b>
                                    /
                                    <b>
                                        {offer.stats.properties_count_total != null &&
                                        !isNaN(offer.stats.properties_count_total)
                                            ? offer.stats.properties_count_total
                                            : "-"}
                                    </b>
                                </Text>

                                <SalesProgressBar
                                    totalCount={offer.stats.properties_count_total}
                                    forSaleCount={offer.stats.properties_count_for_sale}
                                />
                            </div>
                        </div>

                        {/* Monitoring Konkurencji */}
                        <div css={offerBoxMonitoring}>
                            <Text variant="body_copy_2" css={offerBoxMonitoringText}>
                                {t("offer_box.monitoring_text")} <b>{t("offer_box.monitoring_name")}</b>
                            </Text>
                            <Link to={chartsRoutes.pl.contact} onClick={scrollToTop}>
                                <Button variant="outlined_secondary">{t("offer_box.order_monitoring")}</Button>
                            </Link>
                        </div>
                    </div>
                </>
            ) : (
                <div css={[flex("center", "center"), h100]}>
                    <Loader />
                </div>
            )}
        </div>
    );
};

const offerBoxBase = css`
    ${elevation(2)};
    border-radius: ${borderRadius(0, 0, 1, 1)};
    width: 100%;
    height: 100%;
    overflow: hidden;
    ${m(0, "auto")};
    ${flex()};
    ${flexDirection("column")};
    ${backgroundColor("#fff")};
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
        ${elevation(3)};
    }
`;

const offerBoxHeader = css`
    ${flex("flex-start", "flex-start")};
    gap: ${calculateRemSize(2)};

    @media (max-width: 768px) {
        ${flexDirection("column")};
        gap: 0;
    }
`;

const offerBoxImageLink = css`
    display: block;
    flex-shrink: 0;
    width: 40%;

    @media (max-width: 768px) {
        width: 100%;
        max-width: 100%;
    }
`;

const offerBoxImageWrapper = css`
    position: relative;
    width: 100%;
`;

const offerBoxHeaderInfo = css`
    ${p(3, 2)}
    flex: 1;
    min-width: 0;
    ${flex()};
    ${flexDirection("column")};
    justify-content: center;

    @media (max-width: 768px) {
        ${p(0, 2)};
    }
`;

const offerImage = css`
    & > img {
        ${imageResponsive};
    }
`;

const offerBoxVendor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
    ${p(0.5, 0)};
`;

const offerBoxPriceHolder = (theme: Theme) => css`
    ${position("absolute")};
    top: ${calculateRemSize(1)};
    left: ${calculateRemSize(1)};
    ${p(0.5, 1)};
    ${borderRadius(2)};
    ${backgroundColor("#fff")};
    ${color(theme.colors.secondary)};
    font-size: ${calculateRemSize(1.5)};
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    span {
        font-size: ${calculateRemSize(1)};
        font-weight: 400;
    }
`;

const offerBoxAddressWrapper = (theme: Theme) => css`
    ${flex("center", "flex-start")};
    ${pv(0.5)};
    color: ${theme.colors.gray[600]};
`;

const offerBoxSection = css`
    ${pv(1.5)};
`;

const offerBoxSectionHeader = css`
    ${flex("center", "flex-start")};
    gap: ${calculateRemSize(1.5)};
    ${pb(1.5)};

    &:first-of-type {
        ${ml(1)};
    }
`;

const offerBoxPriceRow = css`
    ${flex("center", "space-between")};
    ${pv(0.5)};
    gap: ${calculateRemSize(1)};
`;

const offerBoxLabel = css`
    color: #666;
    font-size: ${calculateRemSize(1.5)};
`;

const offerBoxValue = css`
    color: #333;
    font-size: ${calculateRemSize(1.5)};
    text-align: right;
    white-space: nowrap;

    b {
        font-weight: 600;
    }
`;

const offerBoxMonitoring = (theme: Theme) => css`
    ${pv(2)};
    ${p(2)};
    text-align: center;
    ${flex("center", "center")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(1.5)};
    margin-top: ${calculateRemSize(1.5)};
`;

const offerBoxMonitoringText = css`
    color: #333;
    line-height: 1.6;

    b {
        font-weight: 600;
    }
`;

const offerBoxMonitoringButton = (theme: Theme) => css`
    display: inline-block;
    ${p(1.5, 3)};
    ${borderRadius(2)};
    border: 2px solid ${theme.colors.secondary};
    background-color: #fff;
    color: ${theme.colors.secondary};
    font-size: ${calculateRemSize(1.5)};
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${theme.colors.secondary};
        color: #fff;
        text-decoration: none;
    }
`;

const priceChartWrapper = css`
    margin-top: ${calculateRemSize(2)};
    padding-top: ${calculateRemSize(2)};
    border-top: 1px solid #e0e0e0;
`;

const topSectionsRow = css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${calculateRemSize(2)};

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;
